import routes, { type RouteMap } from './routes';

/**
 * Interface for matched route with extracted parameters
 */
export interface RouteMatch {
  route: RouteMap;
  params: Record<string, string>;
}

/**
 * Custom navigation event name
 */
export const NAVIGATION_EVENT = 'app:navigate';

/**
 * Matches a URL path against route patterns, supporting URL parameters like /wizard/{id}
 * @param routePattern - The route pattern from routes.ts (e.g., "/wizard/{id}")
 * @param urlPath - The actual URL path (e.g., "/wizard/123")
 * @returns Object with matched flag and extracted parameters, or null if no match
 */
function matchRoute(routePattern: string, urlPath: string): { params: Record<string, string> } | null {
  // Split both pattern and URL into segments
  const patternSegments = routePattern.split('/').filter(s => s.length > 0);
  const urlSegments = urlPath.split('/').filter(s => s.length > 0);
  
  // If segment counts don't match, it's not a match
  if (patternSegments.length !== urlSegments.length) {
    return null;
  }
  
  const params: Record<string, string> = {};
  
  // Compare each segment
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const urlSegment = urlSegments[i];
    
    // Check if this is a parameter (enclosed in curly braces)
    if (patternSegment.startsWith('{') && patternSegment.endsWith('}')) {
      // Extract parameter name and store the value
      const paramName = patternSegment.slice(1, -1);
      params[paramName] = urlSegment;
    } else if (patternSegment !== urlSegment) {
      // Static segment doesn't match
      return null;
    }
  }
  
  return { params };
}

/**
 * Finds a matching route for the given URL path
 * @param urlPath - The URL path to match
 * @returns RouteMatch object with route and parameters, or null if no match
 */
export function findMatchingRoute(urlPath: string): RouteMatch | null {
  // Handle empty path or just "/" as root
  const normalizedPath = urlPath === '' || urlPath === '/' ? '/' : urlPath;
  
  for (const route of routes) {
    // First try exact match for efficiency
    if (route.url === normalizedPath) {
      return { route, params: {} };
    }
    
    // Then try pattern matching for parameterized routes
    const match = matchRoute(route.url, normalizedPath);
    if (match) {
      return { route, params: match.params };
    }
  }
  
  return null;
}

/**
 * Navigates to a new route using the History API (SPA-style navigation)
 * @param path - The path to navigate to
 * @param replaceState - If true, replaces current history entry instead of adding new one
 */
export function navigate(path: string, replaceState: boolean = false): void {
  // Don't navigate if we're already on this path
  if (window.location.pathname === path) {
    return;
  }
  
  // Update browser history
  if (replaceState) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
  }
  
  // Dispatch custom navigation event
  window.dispatchEvent(new CustomEvent(NAVIGATION_EVENT, { detail: { path } }));
}

/**
 * Gets the current route match based on current URL
 */
export function getCurrentRoute(): RouteMatch | null {
  return findMatchingRoute(window.location.pathname);
}
